import { useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSites } from '../hooks/useSites'
import { useCameras } from '../hooks/useCameras'
import { useGateways } from '../hooks/useGateways'
import { useCameraHealth } from '../hooks/useCameraHealth'
import { canManageSites } from '../lib/roles'
import { CameraCard } from '../components/camera/CameraCard'
import { AddCameraDialog } from '../components/camera/AddCameraDialog'
import { EditCameraDialog } from '../components/camera/EditCameraDialog'
import { AddGatewayDialog } from '../components/gateway/AddGatewayDialog'
import { EditGatewayDialog } from '../components/gateway/EditGatewayDialog'
import { EditSiteDialog } from '../components/site/EditSiteDialog'
import { CameraSettingsPanel } from '../components/viewer/CameraSettingsPanel'
import { SiteMap } from '../components/site/SiteMap'
import { useToast } from '../components/ui/Toast'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { GridSkeleton } from '../components/ui/Skeleton'
import type { Camera, Gateway } from '../types'

type Tab = 'cameras' | 'map' | 'gateways'

export function SiteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { sites, deleteSite, updateSite } = useSites()
  const { cameras, loading, createCamera, updateCamera, deleteCamera, nextCamNumber } = useCameras(id)
  const { gateways, loading: gwLoading, createGateway, updateGateway, deleteGateway } = useGateways(id)
  const { toast } = useToast()
  const { confirm, dialog: confirmDialog } = useConfirm()
  const [showAddCam, setShowAddCam] = useState(false)
  const [showAddGateway, setShowAddGateway] = useState(false)
  const [showEditSite, setShowEditSite] = useState(false)
  const [editCamera, setEditCamera] = useState<Camera | null>(null)
  const [editGateway, setEditGateway] = useState<Gateway | null>(null)
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null)
  const [tab, setTab] = useState<Tab>('cameras')

  const handleCameraLocationChanged = useCallback(async (cameraId: string, lat: number, lng: number) => {
    try {
      await updateCamera(cameraId, { latitude: lat, longitude: lng })
      toast('Camera location saved', 'success')
    } catch {
      toast('Failed to save camera location', 'error')
    }
  }, [updateCamera, toast])

  const handleCameraLocationRemoved = useCallback(async (cameraId: string) => {
    try {
      await updateCamera(cameraId, { latitude: null, longitude: null })
      toast('Camera removed from map', 'success')
    } catch {
      toast('Failed to remove camera location', 'error')
    }
  }, [updateCamera, toast])

  const handleSaveCamera = useCallback(async (
    cameraId: string,
    updates: Partial<Pick<Camera, 'name' | 'cam_ip' | 'port' | 'external_ip' | 'rtsp_path' | 'username' | 'password'>>,
  ) => {
    await updateCamera(cameraId, updates)
    toast('Camera updated', 'success')
  }, [updateCamera, toast])

  const handleSaveSite = useCallback(async (
    siteId: string,
    updates: Partial<Pick<import('../types').Site, 'name' | 'group_name'>>,
  ) => {
    await updateSite(siteId, updates)
    toast('Site updated', 'success')
  }, [updateSite, toast])

  // Count cameras per gateway for the list view
  const camerasPerGateway = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const cam of cameras) {
      if (cam.gateway_id) counts[cam.gateway_id] = (counts[cam.gateway_id] || 0) + 1
    }
    return counts
  }, [cameras])

  const site = sites.find(s => s.id === id)
  const canManage = profile ? canManageSites(profile.role) : false
  const canSeeGateways = profile ? canManageSites(profile.role) : false
  const { getStatus: getHealthStatus, refreshAll: refreshHealth, loading: healthLoading } = useCameraHealth(site?.org_id ?? null)

  if (!site) {
    return (
      <div className="text-gray-500">
        <p>Site not found.</p>
        <Link to="/" className="text-brand hover:underline text-sm">Back to Dashboard</Link>
      </div>
    )
  }

  async function handleDeleteSite() {
    const ok = await confirm({
      title: 'Delete Site',
      message: `Are you sure you want to delete "${site!.name}" and all its cameras? This cannot be undone.`,
      confirmLabel: 'Delete Site',
    })
    if (!ok) return
    try {
      await deleteSite(site!.id)
      toast('Site deleted', 'success')
      navigate('/')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete site', 'error')
    }
  }

  async function handleDeleteCamera(cam: { id: string; name: string }) {
    const ok = await confirm({
      title: 'Delete Camera',
      message: `Are you sure you want to delete "${cam.name}"? This cannot be undone.`,
      confirmLabel: 'Delete Camera',
    })
    if (!ok) return
    try {
      await deleteCamera(cam.id)
      toast('Camera deleted', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete', 'error')
    }
  }

  async function handleCreateCameras(cams: Parameters<typeof createCamera>[0][]) {
    for (const cam of cams) {
      await createCamera(cam)
    }
  }

  async function handleDeleteGateway(gw: Gateway) {
    const camCount = camerasPerGateway[gw.id] || 0
    const ok = await confirm({
      title: 'Delete Gateway',
      message: `Are you sure you want to delete "${gw.name}"?${camCount > 0 ? ` ${camCount} camera${camCount !== 1 ? 's' : ''} will be unlinked.` : ''} This cannot be undone.`,
      confirmLabel: 'Delete Gateway',
    })
    if (!ok) return
    try {
      await deleteGateway(gw.id)
      toast('Gateway deleted', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete gateway', 'error')
    }
  }

  async function handleSaveGateway(id: string, updates: Partial<Omit<Gateway, 'id' | 'site_id' | 'created_at'>>) {
    await updateGateway(id, updates)
    toast('Gateway updated', 'success')
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Link to="/" className="text-gray-500 hover:text-gray-900 text-sm">&larr; Dashboard</Link>
          </div>
          <h1 className="text-2xl font-semibold">{site.name}</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {cameras.length} camera{cameras.length !== 1 ? 's' : ''}
            {site.group_name ? <span> &middot; {String(site.group_name)}</span> : null}
          </p>
        </div>
        <div className="flex gap-2">
          {cameras.length > 0 && (
            <Link
              to={`/sites/${site.id}/viewer`}
              className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
            >
              Open Viewer
            </Link>
          )}
          {canManage && (
            <>
              <button
                onClick={() => setShowAddCam(true)}
                className="border border-brand text-brand hover:bg-brand/10 text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                Add Camera
              </button>
              <button
                onClick={() => setShowEditSite(true)}
                className="border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                Edit Site
              </button>
              <button
                onClick={handleDeleteSite}
                className="border border-error/30 text-error/70 hover:bg-error/10 text-sm px-4 py-2 rounded-md transition-colors"
              >
                Delete Site
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 mb-4">
        <button
          onClick={() => setTab('cameras')}
          className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
            tab === 'cameras' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
          }`}
        >
          Cameras
        </button>
        <button
          onClick={() => setTab('map')}
          className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
            tab === 'map' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
          }`}
        >
          Map
        </button>
        {canSeeGateways && (
          <button
            onClick={() => setTab('gateways')}
            className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
              tab === 'gateways' ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
          >
            Gateways{gateways.length > 0 ? ` (${gateways.length})` : ''}
          </button>
        )}
      </div>

      {/* Camera grid */}
      {tab === 'cameras' && (
        loading ? (
          <GridSkeleton count={4} />
        ) : cameras.length > 0 ? (
          <>
            <div className="flex justify-end mb-2">
              <button
                onClick={refreshHealth}
                disabled={healthLoading}
                className="text-xs text-gray-500 hover:text-brand transition-colors disabled:opacity-50"
              >
                {healthLoading ? 'Checking...' : 'Check Health'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cameras.map(cam => (
                <CameraCard
                  key={cam.id}
                  camera={cam}
                  siteId={site.id}
                  canManage={canManage}
                  healthStatus={getHealthStatus(cam.id)?.status}
                  healthDetail={getHealthStatus(cam.id)}
                  onEdit={() => setEditCamera(cam)}
                  onDelete={() => handleDeleteCamera(cam)}
                  onSettings={() => setSettingsCamera(cam)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="bg-card border border-card-border rounded-lg p-8 text-center text-gray-500">
            <p className="text-lg mb-2">No cameras</p>
            <p className="text-sm">Click &quot;Add Camera&quot; to configure cameras for this site.</p>
          </div>
        )
      )}

      {/* Map view */}
      {tab === 'map' && (
        <SiteMap
          site={site}
          cameras={cameras}
          canManage={canManage}
          onCameraLocationChanged={handleCameraLocationChanged}
          onCameraLocationRemoved={handleCameraLocationRemoved}
          onSaveDefaultLocation={async (lat, lng) => {
            await updateSite(site.id, { latitude: lat, longitude: lng })
            toast('Default map view saved', 'success')
          }}
        />
      )}

      {/* Gateways tab */}
      {tab === 'gateways' && (
        gwLoading ? (
          <GridSkeleton count={3} />
        ) : (
          <div>
            {canManage && (
              <div className="mb-4">
                <button
                  onClick={() => setShowAddGateway(true)}
                  className="border border-brand text-brand hover:bg-brand/10 text-sm font-medium px-4 py-2 rounded-md transition-colors"
                >
                  Add Gateway
                </button>
              </div>
            )}

            {gateways.length > 0 ? (
              <div className="space-y-2">
                {gateways.map(gw => {
                  const camCount = camerasPerGateway[gw.id] || 0
                  return (
                    <div key={gw.id} className="bg-card border border-card-border rounded-lg px-4 py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-900 truncate">{gw.name}</span>
                          {gw.sim_only && (
                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">SIM</span>
                          )}
                          {gw.carrier && (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{gw.carrier}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="font-mono">{gw.droplet_ip}:{gw.go2rtc_port}</span>
                          {gw.router_ip && <span>Router: <span className="font-mono">{gw.router_ip}</span></span>}
                          {gw.tunnel_ip && <span>Tunnel: <span className="font-mono">{gw.tunnel_ip}</span></span>}
                          <span>{camCount} cam{camCount !== 1 ? 's' : ''}</span>
                          {gw.rsrp !== null && (
                            <span title={`RSSI: ${gw.rssi ?? '—'} | RSRP: ${gw.rsrp} | RSRQ: ${gw.rsrq ?? '—'} | SINR: ${gw.sinr ?? '—'}`}>
                              Signal: {gw.rsrp} dBm
                            </span>
                          )}
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditGateway(gw)}
                            className="text-xs text-brand hover:underline"
                          >
                            Edit
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handleDeleteGateway(gw)}
                            className="text-xs text-error/70 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="bg-card border border-card-border rounded-lg p-8 text-center text-gray-500">
                <p className="text-lg mb-2">No gateways</p>
                <p className="text-sm">Gateways represent the router + droplet + SIM connection for cameras at this site.</p>
              </div>
            )}
          </div>
        )
      )}

      <AddCameraDialog
        open={showAddCam}
        onClose={() => setShowAddCam(false)}
        siteName={site.name}
        siteId={site.id}
        nextCamNumber={nextCamNumber}
        defaultExternalIp={cameras.find(c => c.external_ip)?.external_ip ?? ''}
        onCreated={handleCreateCameras}
      />

      <EditCameraDialog
        open={!!editCamera}
        camera={editCamera}
        onClose={() => setEditCamera(null)}
        onSave={handleSaveCamera}
      />

      <EditSiteDialog
        open={showEditSite}
        site={site}
        onClose={() => setShowEditSite(false)}
        onSave={handleSaveSite}
      />

      <AddGatewayDialog
        open={showAddGateway}
        onClose={() => setShowAddGateway(false)}
        siteId={site.id}
        onCreate={createGateway}
      />

      <EditGatewayDialog
        open={!!editGateway}
        gateway={editGateway}
        onClose={() => setEditGateway(null)}
        onSave={handleSaveGateway}
      />

      {settingsCamera && (
        <CameraSettingsPanel
          camera={settingsCamera}
          onClose={() => setSettingsCamera(null)}
        />
      )}

      {confirmDialog}
    </div>
  )
}
